import { OktaAuth } from '@okta/okta-auth-js';

// `groups` IS a valid scope on Adobe's oktapreview tenant — it's what
// makes the ID token carry the user's IAM groups so CallbackPage can
// gate on GRP-APS-AJO-DESIGN-AGENT-DEV-USERS. The earlier 400 Bad
// Request was caused by pointing at adobe.okta.com (prod) instead of
// adobe.oktapreview.com (dev); not by the scope.
//
// tokenManager.autoRenew + expireEarlySeconds: long-running flows
// (auto-run, multi-stage pipelines, big BRDs) routinely outlast the
// default 1hr Okta access-token TTL. Without proactive renewal, every
// call mid-session that races past `exp` returns AgentCore HTTP 401.
// `autoRenew: true` makes okta-auth-js refresh in the background; the
// 120s skew kicks renewal off *before* the token expires so in-flight
// requests don't fail.
export const oktaAuth = new OktaAuth({
  clientId: import.meta.env.VITE_OKTA_CLIENT_ID,
  issuer: import.meta.env.VITE_OKTA_ISSUER,
  redirectUri: `${window.location.origin}/login/callback`,
  scopes: ['openid', 'profile', 'email', 'groups'],
  pkce: true,
  tokenManager: {
    autoRenew: true,
    expireEarlySeconds: 120,
  },
});

/**
 * Best-effort: returns the current access token, renewing first if it
 * is expired or about to expire. Falls back to the raw cached token if
 * renewal fails so callers can still attempt the request and surface
 * the 401 path.
 *
 * LOCAL DEV FALLBACK: When running with VITE_OKTA_MOCK=true (no real
 * Okta session), you can inject a real dev token grabbed from the live
 * site so API calls succeed. Precedence:
 *   1. Real Okta token (production path, always preferred)
 *   2. localStorage.getItem('ajo_dev_token')
 *   3. import.meta.env.VITE_DEV_BEARER
 * Set the token in devtools console while on localhost:
 *   localStorage.setItem('ajo_dev_token', '<paste-token-here>')
 * To grab the token from the live dev site, open devtools console on
 * https://ajo-design-agent.dev.apps.aps-ai-eng.adobe.net and run:
 *   JSON.parse(localStorage['okta-token-storage']).accessToken.accessToken
 */
export async function getAccessTokenJwt(): Promise<string | undefined> {
  try {
    let t = await oktaAuth.tokenManager.get('accessToken');
    if (t) {
      const expiresAt = (t as { expiresAt?: number }).expiresAt || 0;
      const now = Math.floor(Date.now() / 1000);
      // 60s skew — if the token is already expired or fires within a
      // minute, force a renew before handing it to the caller. The
      // tokenManager's autoRenew handles the proactive case, but a
      // tab woken from background sleep can miss the auto-fire and
      // arrive here with a stale token.
      if (!expiresAt || expiresAt - now < 60) {
        try {
          t = await oktaAuth.tokenManager.renew('accessToken') as typeof t;
        } catch {
          // Fall through with whatever we have. The 401 retry path in
          // service callers will give it one more shot.
        }
      }
    }
    const oktaToken = (t as { accessToken?: string } | undefined)?.accessToken;
    // Real Okta token always wins (production path). Only when we have NO
    // Okta session (VITE_OKTA_MOCK=true or fresh dev server) do we check
    // the dev-token fallback.
    if (oktaToken) return oktaToken;

    // LOCAL DEV ONLY: fall back to manually-injected token for localhost
    // testing against the real dev API when running in mock mode.
    const devToken = getDevToken();
    return devToken || undefined;
  } catch {
    return undefined;
  }
}

/**
 * LOCAL DEV ONLY: retrieve a manually-injected dev bearer token from
 * localStorage or env var. This lets developers paste a real Okta
 * access token (grabbed from the live site where they're already logged
 * in) and test against the real dev API locally without going through
 * the full Okta redirect flow on localhost.
 *
 * Emits a one-time console instruction the first time it's called in
 * mock mode with no token present.
 */
let _devTokenInstructionShown = false;

// Decode a JWT's `exp` and report whether it's already past (with a small
// skew). Used to skip a STALE localStorage dev token so it doesn't
// silently shadow a fresher VITE_DEV_BEARER in .env.local — the #1 source
// of "I pasted a new token but still get 403" confusion. Best-effort: a
// token we can't decode is treated as not-expired (let the API reject it).
function isJwtExpired(jwt: string): boolean {
  try {
    const payload = JSON.parse(
      atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    const exp = Number(payload?.exp) || 0;
    if (!exp) return false;
    return exp - Math.floor(Date.now() / 1000) < 60;
  } catch {
    return false;
  }
}

function getDevToken(): string | null {
  const isMock = import.meta.env.VITE_OKTA_MOCK === 'true';
  const rawLocalToken = typeof localStorage !== 'undefined'
    ? localStorage.getItem('ajo_dev_token')
    : null;
  // Skip an expired localStorage token so it falls through to the env var
  // (two independent fallbacks instead of localStorage always winning).
  const localToken = rawLocalToken && !isJwtExpired(rawLocalToken)
    ? rawLocalToken
    : null;
  if (rawLocalToken && !localToken) {
    console.warn(
      '[AJO Design Agent] localStorage ajo_dev_token is expired — ' +
        'falling back to VITE_DEV_BEARER. Re-grab a fresh token to use localStorage.',
    );
  }
  const envToken = import.meta.env.VITE_DEV_BEARER || '';

  const token = localToken || (envToken.trim() ? envToken : null);

  // Emit helpful instructions ONCE when running in mock mode with no
  // token — surface the 401 cause + fix instructions.
  if (isMock && !token && !_devTokenInstructionShown) {
    _devTokenInstructionShown = true;
    console.info(
      '%c[AJO Design Agent] Mock mode + no dev token → API calls will 401',
      'color: orange; font-weight: bold;',
      '\n\nTo test against the real dev API from localhost:',
      '\n1. Open https://ajo-design-agent.dev.apps.aps-ai-eng.adobe.net in a new tab',
      '\n2. Sign in (or verify you\'re already signed in)',
      '\n3. Open devtools console and run:',
      '\n   JSON.parse(localStorage[\'okta-token-storage\']).accessToken.accessToken',
      '\n4. Copy the printed token',
      '\n5. Back on localhost, paste it into devtools console:',
      '\n   localStorage.setItem(\'ajo_dev_token\', \'<paste-token-here>\')',
      '\n6. Refresh localhost',
      '\n\nThe token is valid for ~1 hour. When it expires, repeat steps 3-6.'
    );
  }

  return token;
}

/**
 * Force-refresh the access token and return the new JWT. Use this in
 * 401 retry paths — by then we know the cached token is no good.
 */
export async function renewAccessTokenJwt(): Promise<string | undefined> {
  try {
    const t = await oktaAuth.tokenManager.renew('accessToken');
    return (t as { accessToken?: string } | undefined)?.accessToken;
  } catch {
    return undefined;
  }
}
